import React, { useState } from 'react'
import { omapApi } from '../../services/omapApi'
import './ExportPlaybookModal.css'

interface ExportConfig {
  include_executive_summary: boolean
  include_mece_pillars: boolean
  include_evidence: boolean
  include_annotations: boolean
  include_strategy_map: boolean
  include_roadmap: boolean
  filter_mode: 'all' | 'in_progress' | 'prioritized' | 'completed' | 'not_started'
  format: 'markdown' | 'html'
}

interface ExportPlaybookModalProps {
  isOpen: boolean
  conversationId: string | number
  onClose: () => void
  onExportComplete?: (content: string, format: string) => void
}

export const ExportPlaybookModal: React.FC<ExportPlaybookModalProps> = ({
  isOpen,
  conversationId,
  onClose,
  onExportComplete
}) => {
  const [config, setConfig] = useState<ExportConfig>({
    include_executive_summary: true,
    include_mece_pillars: true,
    include_evidence: true,
    include_annotations: true,
    include_strategy_map: true,
    include_roadmap: false,
    filter_mode: 'all',
    format: 'markdown'
  })
  const [isExporting, setIsExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!isOpen) return null

  const handleExport = async () => {
    setIsExporting(true)
    setError(null)

    try {
      const result = await omapApi.exportPlaybook(conversationId, config)

      if (result.success) {
        // Trigger download
        const blob = new Blob([result.content], {
          type: result.format === 'html' ? 'text/html' : 'text/markdown'
        })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `strategy-playbook.${result.format === 'html' ? 'html' : 'md'}`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)

        onExportComplete?.(result.content, result.format)
        onClose()
      } else {
        setError('Failed to generate playbook')
      }
    } catch (err: any) {
      console.error('[ExportPlaybook] Export failed:', err)
      setError(err.message || 'Failed to export playbook')
    } finally {
      setIsExporting(false)
    }
  }

  const handleCopyToClipboard = async () => {
    setIsExporting(true)
    setError(null)

    try {
      const result = await omapApi.exportPlaybook(conversationId, {
        ...config,
        format: 'markdown' // Always copy markdown
      })

      if (result.success) {
        await navigator.clipboard.writeText(result.content)
        onClose()
      } else {
        setError('Failed to generate playbook')
      }
    } catch (err: any) {
      console.error('[ExportPlaybook] Copy failed:', err)
      setError(err.message || 'Failed to copy playbook')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="export-modal-overlay" onClick={onClose}>
      <div className="export-modal" onClick={(e) => e.stopPropagation()}>
        <div className="export-modal-header">
          <h2>Export Strategy Playbook</h2>
          <button className="btn-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        <div className="export-modal-body">
          {error && (
            <div className="export-error">
              <span className="error-icon">!</span>
              {error}
            </div>
          )}

          <section className="config-section">
            <h3>Include in Export</h3>
            <label className="config-checkbox">
              <input
                type="checkbox"
                checked={config.include_executive_summary}
                onChange={(e) => setConfig({
                  ...config,
                  include_executive_summary: e.target.checked
                })}
              />
              <span>Executive Summary</span>
            </label>
            <label className="config-checkbox">
              <input
                type="checkbox"
                checked={config.include_mece_pillars}
                onChange={(e) => setConfig({
                  ...config,
                  include_mece_pillars: e.target.checked
                })}
              />
              <span>Strategic Pillars & Tactics</span>
            </label>
            <label className="config-checkbox sub-option">
              <input
                type="checkbox"
                checked={config.include_evidence}
                onChange={(e) => setConfig({
                  ...config,
                  include_evidence: e.target.checked
                })}
                disabled={!config.include_mece_pillars}
              />
              <span>Include supporting evidence</span>
            </label>
            <label className="config-checkbox sub-option">
              <input
                type="checkbox"
                checked={config.include_annotations}
                onChange={(e) => setConfig({
                  ...config,
                  include_annotations: e.target.checked
                })}
                disabled={!config.include_mece_pillars}
              />
              <span>Include my notes/annotations</span>
            </label>
            <label className="config-checkbox">
              <input
                type="checkbox"
                checked={config.include_strategy_map}
                onChange={(e) => setConfig({
                  ...config,
                  include_strategy_map: e.target.checked
                })}
              />
              <span>Strategy Map Overview</span>
            </label>
            <label className="config-checkbox">
              <input
                type="checkbox"
                checked={config.include_roadmap}
                onChange={(e) => setConfig({
                  ...config,
                  include_roadmap: e.target.checked
                })}
              />
              <span>Implementation Roadmap</span>
            </label>
          </section>

          <section className="config-section">
            <h3>Filter Tactics</h3>
            <label className="config-radio">
              <input
                type="radio"
                name="filter"
                checked={config.filter_mode === 'all'}
                onChange={() => setConfig({ ...config, filter_mode: 'all' })}
              />
              <span>All tactics</span>
            </label>
            <label className="config-radio">
              <input
                type="radio"
                name="filter"
                checked={config.filter_mode === 'prioritized'}
                onChange={() => setConfig({ ...config, filter_mode: 'prioritized' })}
              />
              <span>Only tactics I've prioritized</span>
            </label>
            <label className="config-radio">
              <input
                type="radio"
                name="filter"
                checked={config.filter_mode === 'in_progress'}
                onChange={() => setConfig({ ...config, filter_mode: 'in_progress' })}
              />
              <span>Only "Planning" or "In Progress"</span>
            </label>
            <label className="config-radio">
              <input
                type="radio"
                name="filter"
                checked={config.filter_mode === 'completed'}
                onChange={() => setConfig({ ...config, filter_mode: 'completed' })}
              />
              <span>Only "Completed"</span>
            </label>
            <label className="config-radio">
              <input
                type="radio"
                name="filter"
                checked={config.filter_mode === 'not_started'}
                onChange={() => setConfig({ ...config, filter_mode: 'not_started' })}
              />
              <span>Only "Not Started"</span>
            </label>
          </section>

          <section className="config-section">
            <h3>Format</h3>
            <label className="config-radio">
              <input
                type="radio"
                name="format"
                checked={config.format === 'markdown'}
                onChange={() => setConfig({ ...config, format: 'markdown' })}
              />
              <span>Markdown (.md)</span>
            </label>
            <label className="config-radio">
              <input
                type="radio"
                name="format"
                checked={config.format === 'html'}
                onChange={() => setConfig({ ...config, format: 'html' })}
              />
              <span>HTML (.html)</span>
            </label>
          </section>
        </div>

        <div className="export-modal-footer">
          <button className="btn-cancel" onClick={onClose} disabled={isExporting}>
            Cancel
          </button>
          <button
            className="btn-copy"
            onClick={handleCopyToClipboard}
            disabled={isExporting}
            title="Copy markdown to clipboard"
          >
            {isExporting ? 'Copying...' : 'Copy to Clipboard'}
          </button>
          <button
            className="btn-export"
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting ? 'Generating...' : 'Download Playbook'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ExportPlaybookModal
